/**
 * CR318 + CR322: Live Preview Health Banner
 *
 * Enhanced health banner with:
 * - Overall status chip & pills
 * - CR322: Edge metadata, edge probe, supabase config, debug summary
 */

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Globe,
  Wifi,
  Lock,
  Database,
  Rocket,
  Activity,
  Zap,
  Settings,
} from "lucide-react";
import { useProductionHealth, type HealthStatus } from "@/hooks/useProductionHealth";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

interface LivePreviewHealthBannerProps {
  defaultExpanded?: boolean;
}

export function LivePreviewHealthBanner({ defaultExpanded = false }: LivePreviewHealthBannerProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);

  const health = useProductionHealth();
  const {
    classification, checks, updatedAt, isRunning,
    edgeProbe, supabaseConfig, edgeDebugSummary,
    runTriage, copyDiagnostics,
  } = health;

  useEffect(() => {
    if (searchParams.get("openHealth") === "1") {
      setExpanded(true);
      setTimeout(() => {
        document.getElementById("health-banner")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [searchParams]);

  const handleCopy = async () => {
    const success = await copyDiagnostics();
    if (success) {
      setCopied(true);
      toast.success("Diagnostics copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Failed to copy diagnostics");
    }
  };

  const getStatusColor = (status: HealthStatus) => {
    switch (status) {
      case "healthy": return "bg-green-500/10 border-green-500/30";
      case "degraded": return "bg-amber-500/10 border-amber-500/30";
      case "outage": return "bg-red-500/10 border-red-500/30";
    }
  };

  const getStatusIcon = (status: HealthStatus) => {
    switch (status) {
      case "healthy": return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case "degraded": return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case "outage": return <XCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const lastCheckedText = updatedAt
    ? formatDistanceToNow(new Date(updatedAt), { addSuffix: true })
    : "Never";

  const edge = checks.edge;

  return (
    <Alert
      id="health-banner"
      className={`border-2 ${getStatusColor(classification.status)}`}
    >
      <Activity className="h-4 w-4" />
      <AlertTitle className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {getStatusIcon(classification.status)}
          <span className="font-bold">
            {classification.status === "healthy" ? "Production Healthy" : classification.label || "Production Issue"}
          </span>
          <Badge variant="outline" className="text-xs ml-2">
            Checked {lastCheckedText}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={runTriage} disabled={isRunning}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isRunning ? "animate-spin" : ""}`} />
            {isRunning ? "Running..." : "Run Triage"}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleCopy}>
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </AlertTitle>
      <AlertDescription className="mt-2">
        <p className="text-sm mb-3">{classification.reason}</p>
        {classification.secondaryReason && (
          <p className="text-sm text-muted-foreground mb-3">{classification.secondaryReason}</p>
        )}

        {/* Status Pills */}
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge variant="outline" className={checks.snapshot?.status === "OK" ? "bg-green-500/10 text-green-700 border-green-500/30" : checks.snapshot?.status === "MISSING" ? "bg-red-500/10 text-red-700 border-red-500/30" : "bg-amber-500/10 text-amber-700 border-amber-500/30"}>
            <Globe className="h-3 w-3 mr-1" />
            Snapshot: {checks.snapshot?.status || "?"}
          </Badge>
          <Badge variant="outline" className={edge?.status === "OK" ? "bg-green-500/10 text-green-700 border-green-500/30" : "bg-red-500/10 text-red-700 border-red-500/30"}>
            <Wifi className="h-3 w-3 mr-1" />
            Edge: {edge?.status || "?"} {edge?.latencyMs && `(${edge.latencyMs}ms)`}
          </Badge>
          <Badge variant="outline" className={checks.auth?.status === "OK" ? "bg-green-500/10 text-green-700 border-green-500/30" : "bg-amber-500/10 text-amber-700 border-amber-500/30"}>
            <Lock className="h-3 w-3 mr-1" />
            Auth: {checks.auth?.status || "?"}
          </Badge>
          <Badge variant="outline" className={checks.db?.status === "OK" ? "bg-green-500/10 text-green-700 border-green-500/30" : "bg-red-500/10 text-red-700 border-red-500/30"}>
            <Database className="h-3 w-3 mr-1" />
            DB: {checks.db?.status || "?"} {checks.db?.latencyMs && `(${checks.db.latencyMs}ms)`}
          </Badge>
          {/* CR322: Edge Probe pill */}
          {edgeProbe && (
            <Badge variant="outline" className={edgeProbe.status === "OK" ? "bg-green-500/10 text-green-700 border-green-500/30" : edgeProbe.status === "ERROR" ? "bg-red-500/10 text-red-700 border-red-500/30" : "bg-muted text-muted-foreground"}>
              <Zap className="h-3 w-3 mr-1" />
              Probe: {edgeProbe.status} {edgeProbe.latencyMs != null && `(${edgeProbe.latencyMs}ms)`}
            </Badge>
          )}
        </div>

        {classification.label === "No Snapshot" && (
          <Button size="sm" onClick={() => navigate("/admin/batch-runs")} className="mr-2">
            <Rocket className="h-3.5 w-3.5 mr-1" />
            Go to Batch Runs
          </Button>
        )}

        {/* CR322: Debug Summary (one-line) */}
        {edgeDebugSummary && (
          <div className="p-2 bg-muted/50 rounded border text-[11px] font-mono text-muted-foreground mb-3 break-all">
            {edgeDebugSummary}
          </div>
        )}

        {/* Expandable Endpoint Debug */}
        <Collapsible open={expanded} onOpenChange={setExpanded}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="text-xs">
              {expanded ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
              {expanded ? "Hide" : "Show"} Endpoint Debug
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3 space-y-3">
            {/* Snapshot Details */}
            <div className="p-3 bg-background rounded-lg border text-xs font-mono space-y-1">
              <div className="font-semibold text-foreground flex items-center gap-2">
                <Globe className="h-3.5 w-3.5" />
                Snapshot
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div><span className="text-muted-foreground">ID:</span> {checks.snapshot?.snapshotId?.slice(0, 24) || "null"}...</div>
                <div><span className="text-muted-foreground">Published:</span> {checks.snapshot?.publishedAt || "null"}</div>
                <div><span className="text-muted-foreground">Deals:</span> {checks.snapshot?.dealCount ?? "null"}</div>
                <div><span className="text-muted-foreground">Status:</span> {checks.snapshot?.status || "?"}</div>
              </div>
            </div>

            {/* Edge Details (CR322 expanded) */}
            <div className="p-3 bg-background rounded-lg border text-xs font-mono space-y-1">
              <div className="font-semibold text-foreground flex items-center gap-2">
                <Wifi className="h-3.5 w-3.5" />
                Edge Function
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="col-span-2"><span className="text-muted-foreground">Function:</span> {edge?.resolvedFunctionName || "N/A"}</div>
                <div className="col-span-2 break-all"><span className="text-muted-foreground">URL:</span> {edge?.resolvedEdgeUrl || "N/A"}</div>
                <div><span className="text-muted-foreground">Method:</span> {edge?.requestMethod || "N/A"}</div>
                <div><span className="text-muted-foreground">HTTP Status:</span> {edge?.httpStatus || "N/A"}</div>
                <div><span className="text-muted-foreground">Latency:</span> {edge?.latencyMs ?? "N/A"}ms</div>
                <div><span className="text-muted-foreground">Auth Header:</span> {edge?.requestHeadersPresent?.authorization ? "Yes" : "No"}</div>
                <div><span className="text-muted-foreground">Content-Type:</span> {edge?.requestHeadersPresent?.contentType ? "Yes" : "No"}</div>
                <div><span className="text-muted-foreground">Invoke:</span> {edge?.invokeAttempted ? "Yes" : "No"}</div>
              </div>
              {edge?.fetchError && (
                <div className="mt-2 p-2 bg-red-500/5 rounded border border-red-500/20">
                  <div className="text-red-500 font-semibold">{edge.fetchError.name}: {edge.fetchError.message}</div>
                  {edge.fetchError.cause && <div className="text-muted-foreground mt-1">Cause: {edge.fetchError.cause}</div>}
                  {edge.fetchError.stack && <div className="text-muted-foreground mt-1 text-[10px] max-h-20 overflow-y-auto whitespace-pre-wrap">{edge.fetchError.stack}</div>}
                </div>
              )}
              {edge?.payloadSnippet && (
                <div className="mt-2"><span className="text-muted-foreground">Payload:</span> {edge.payloadSnippet}</div>
              )}
            </div>

            {/* CR322: Edge Probe */}
            {edgeProbe && (
              <div className="p-3 bg-background rounded-lg border text-xs font-mono space-y-1">
                <div className="font-semibold text-foreground flex items-center gap-2">
                  <Zap className="h-3.5 w-3.5" />
                  Edge Probe
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="col-span-2"><span className="text-muted-foreground">Function:</span> {edgeProbe.functionName}</div>
                  <div><span className="text-muted-foreground">Status:</span> {edgeProbe.status}</div>
                  <div><span className="text-muted-foreground">HTTP:</span> {edgeProbe.httpStatus ?? "N/A"}</div>
                  <div><span className="text-muted-foreground">Latency:</span> {edgeProbe.latencyMs ?? "N/A"}ms</div>
                  <div><span className="text-muted-foreground">Attempted:</span> {edgeProbe.attempted ? "Yes" : "No"}</div>
                </div>
                {edgeProbe.error && (
                  <div className="text-red-500 mt-2"><span className="text-muted-foreground">Error:</span> {edgeProbe.error}</div>
                )}
              </div>
            )}

            {/* CR322: Supabase Config Snapshot */}
            {supabaseConfig && (
              <div className="p-3 bg-background rounded-lg border text-xs font-mono space-y-1">
                <div className="font-semibold text-foreground flex items-center gap-2">
                  <Settings className="h-3.5 w-3.5" />
                  Client Config
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="col-span-2 break-all"><span className="text-muted-foreground">URL:</span> {supabaseConfig.url || "null"}</div>
                  <div><span className="text-muted-foreground">Anon Key:</span> {supabaseConfig.anonKeyPresent ? "Present" : "Missing"}</div>
                  <div><span className="text-muted-foreground">Project Ref:</span> {supabaseConfig.projectRefDerived || "null"}</div>
                  <div><span className="text-muted-foreground">Init Source:</span> {supabaseConfig.clientInitSource || "null"}</div>
                </div>
              </div>
            )}

            {/* Auth Details */}
            <div className="p-3 bg-background rounded-lg border text-xs font-mono space-y-1">
              <div className="font-semibold text-foreground flex items-center gap-2">
                <Lock className="h-3.5 w-3.5" />
                Authentication
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div><span className="text-muted-foreground">Session:</span> {checks.auth?.hasSession ? "Yes" : "No"}</div>
                <div><span className="text-muted-foreground">Token:</span> {checks.auth?.hasToken ? "Present" : "Missing"}</div>
              </div>
            </div>

            {/* DB Details */}
            <div className="p-3 bg-background rounded-lg border text-xs font-mono space-y-1">
              <div className="font-semibold text-foreground flex items-center gap-2">
                <Database className="h-3.5 w-3.5" />
                Database Sanity
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div><span className="text-muted-foreground">Status:</span> {checks.db?.status || "?"}</div>
                <div><span className="text-muted-foreground">Latency:</span> {checks.db?.latencyMs ?? "N/A"}ms</div>
              </div>
              {checks.db?.message && (
                <div className="text-red-500 mt-2"><span className="text-muted-foreground">Error:</span> {checks.db.message}</div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </AlertDescription>
    </Alert>
  );
}
